import React from 'react';
import Loader from 'react-loader-spinner';
import styled, { css } from 'styled-components';
import { Colors, remInPx } from '.';
import { Copy, Flexer } from '../app/dashboard/elements/LayoutElements';
import { FadeIn } from './Transitions';

const LoaderMount = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Frame = styled.div<{ active: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    position: relative;
    ${p => p.active ? css`
        pointer-events: none;
        & > * { 
            opacity: .5;
        }
    ` : css`
        pointer-events: all;
    `}
`;

export type LoadingProps = {
    loading: boolean,
    hideSpinner?: boolean,
    children?: React.ReactNode,
};

export const LoadingGuard = React.memo<LoadingProps>(({ loading, hideSpinner, children }) =>
    <Frame active={loading}>
        {loading && <LoaderMount>
            {!hideSpinner && <Loader
                type="TailSpin"
                color={Colors.primaryText}
                height={remInPx(3)}
                width={remInPx(3)}
            />}
        </LoaderMount>}
        {children}
    </Frame>
);

const LoadingNextFrame = styled.div<{ visible?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: .5rem;
    padding: .5rem 1rem;
    border-radius: .5rem;
    background: ${Colors.primaryLight};
    opacity: 0;
    transition: opacity 200ms;
    ${p => p.visible && css`
        opacity: 1;
    `}
`;

export const LoadingNext = React.memo<LoadingProps>(({ loading, hideSpinner, children }) =>
    <LoadingNextFrame visible={loading}>
        {!hideSpinner && <Loader
            type="TailSpin"
            color={Colors.white}
            height={remInPx(1.25)}
            width={remInPx(1.25)}
        />}
        <Copy flush color="white" bold size=".875">
            {children ? <>{children}</> : <>Loading</>}
        </Copy>
    </LoadingNextFrame>
);

export const LoadingFrame = ({ delay }: { delay?: number }) =>
    <Flexer flex="column" grow center>
        <FadeIn after={`${delay ?? 500}ms`}>
            <SpotLoader />
        </FadeIn>
    </Flexer>;

const SpotLoader = () =>
    <Spinner>
        <Bouncer mode="A" color={Colors.primary} />
        <Bouncer mode="B" color={Colors.primary} />
    </Spinner>;

const Spinner = styled.div`
    width: 4rem;
    height: 4rem;
    position: relative;
    margin: 10rem auto;
`;

const Bouncer = styled.div<{ mode: 'A' | 'B', color: string }>`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-color: ${p => p.color};
    opacity: 0.6;
    position: absolute;
    top: 0;
    left: 0;

    @keyframes sk-bounce {
        0%, 100% {
            transform: scale(0.0);
            -webkit-transform: scale(0.0);
        } 50% {
            transform: scale(1.0);
            -webkit-transform: scale(1.0);
        }
    }

    animation: sk-bounce 2.0s infinite ease-in-out;
    animation-delay: ${p => p.mode === 'A' ? '0' : '-1.0s'};
`;
